import React, { useState } from 'react';
import './UserPreferences.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function validatePassword(pw) {
  if (pw.length < 12) return 'Password must be at least 12 characters.';
  if (!/[A-Z]/.test(pw)) return 'Password must contain at least one uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Password must contain at least one lowercase letter.';
  if (!/\d/.test(pw)) return 'Password must contain at least one number.';
  if (!/[^A-Za-z\d]/.test(pw)) return 'Password must contain at least one special character.';
  return null;
}

function authFetch(path, options = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers,
    },
  });
}

export default function UserPreferences({ user, onUserUpdate }) {
  const [name, setName] = useState(user?.name || '');
  const [nameStatus, setNameStatus] = useState(null);
  const [nameSaving, setNameSaving] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [newPwError, setNewPwError] = useState(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [pwStatus, setPwStatus] = useState(null);
  const [pwSaving, setPwSaving] = useState(false);

  const handleNameSave = async (e) => {
    e.preventDefault();
    setNameStatus(null);
    if (!name.trim()) { setNameStatus({ error: 'Name cannot be empty.' }); return; }
    if (name.trim() === user?.name) { setNameStatus({ error: 'No change detected.' }); return; }
    setNameSaving(true);
    try {
      const res = await authFetch('/api/auth/me', {
        method: 'PUT',
        body: JSON.stringify({ name: name.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update name');
      setNameStatus({ ok: 'Name updated successfully.' });
      onUserUpdate?.(data.user);
    } catch (err) {
      setNameStatus({ error: err.message });
    } finally {
      setNameSaving(false);
    }
  };

  const handleNewPasswordBlur = () => {
    if (!newPassword) return;
    setNewPwError(validatePassword(newPassword));
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    setPwStatus(null);
    const err = validatePassword(newPassword);
    if (err) { setNewPwError(err); return; }
    setNewPwError(null);
    if (newPassword !== confirmPassword) { setPwStatus({ error: 'Passwords do not match.' }); return; }
    setPwSaving(true);
    try {
      const res = await authFetch('/api/auth/me', {
        method: 'PUT',
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update password');
      setPwStatus({ ok: 'Password changed successfully.' });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setNewPwError(null);
    } catch (err) {
      setPwStatus({ error: err.message });
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="user-prefs">
      <div className="user-prefs-header">
        <h1>Account Settings</h1>
        <p>Manage your profile and security preferences.</p>
      </div>

      <div className="user-prefs-grid">

        {/* Profile card */}
        <div className="prefs-card">
          <div className="prefs-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Profile
          </div>

          <form onSubmit={handleNameSave} className="prefs-form" noValidate>
            <label className="prefs-label">
              Email
              <input className="prefs-input prefs-input--readonly" type="text" value={user?.email || ''} readOnly />
            </label>

            <label className="prefs-label">
              Display Name
              <input
                className="prefs-input"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
            </label>

            {nameStatus?.error && <div className="prefs-msg prefs-msg--error">{nameStatus.error}</div>}
            {nameStatus?.ok    && <div className="prefs-msg prefs-msg--ok">{nameStatus.ok}</div>}

            <button className="prefs-btn" type="submit" disabled={nameSaving}>
              {nameSaving ? 'Saving…' : 'Save Name'}
            </button>
          </form>
        </div>

        {/* Password card */}
        <div className="prefs-card">
          <div className="prefs-card-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <rect x="3" y="11" width="18" height="10" rx="2"/>
              <path d="M7 11V8a5 5 0 0 1 10 0v3"/>
            </svg>
            Change Password
          </div>

          <form onSubmit={handlePasswordSave} className="prefs-form" noValidate>
            <label className="prefs-label">
              Current Password
              <div className="prefs-input-wrap">
                <input
                  className="prefs-input"
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Current password"
                />
                <button type="button" className="prefs-eye" onClick={() => setShowCurrent(s => !s)} aria-label={showCurrent ? 'Hide' : 'Show'}>
                  {showCurrent ? (
                    <svg width="18" height="18" viewBox="-1 -1 26 26" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" fill="none"/>
                      <path d="M4 4L20 20"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="-1 -1 26 26" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" fill="none"/>
                      <circle cx="12" cy="12" r="3" fill="none"/>
                    </svg>
                  )}
                </button>
              </div>
            </label>

            <label className="prefs-label">
              New Password
              <div className="prefs-input-wrap">
                <input
                  className={`prefs-input${newPwError ? ' prefs-input--error' : ''}`}
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e.target.value); if (newPwError) setNewPwError(null); }}
                  onBlur={handleNewPasswordBlur}
                  placeholder="New password"
                />
                <button type="button" className="prefs-eye" onClick={() => setShowNew(s => !s)} aria-label={showNew ? 'Hide' : 'Show'}>
                  {showNew ? (
                    <svg width="18" height="18" viewBox="-1 -1 26 26" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" fill="none"/>
                      <path d="M4 4L20 20"/>
                    </svg>
                  ) : (
                    <svg width="18" height="18" viewBox="-1 -1 26 26" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                      <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" fill="none"/>
                      <circle cx="12" cy="12" r="3" fill="none"/>
                    </svg>
                  )}
                </button>
              </div>
              {newPwError && <div className="prefs-msg prefs-msg--error prefs-msg--field">{newPwError}</div>}
            </label>

            <label className="prefs-label">
              Confirm New Password
              <input
                className="prefs-input"
                type={showNew ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm new password"
              />
            </label>

            <p className="prefs-hint">
              Must be 12+ characters with uppercase, lowercase, a number, and a special character.
            </p>

            {pwStatus?.error && <div className="prefs-msg prefs-msg--error">{pwStatus.error}</div>}
            {pwStatus?.ok    && <div className="prefs-msg prefs-msg--ok">{pwStatus.ok}</div>}

            <button className="prefs-btn" type="submit" disabled={pwSaving}>
              {pwSaving ? 'Saving…' : 'Change Password'}
            </button>
          </form>
        </div>

      </div>
    </div>
  );
}
